'use client'

import { useEffect, useRef, useState } from 'react'

export interface UseActiveSectionOptions {
  /**
   * Suppression window (ms) after `notifyClick()` during which scroll events
   * are ignored. Prevents the highlight from flickering back to the previous
   * section while a smooth-scroll animation is mid-flight.
   */
  ignoreScrollMs?: number
}

export interface UseActiveSectionResult {
  /** Index of the currently active section, or `null` if none. */
  activeIndex: number | null
  /** Call when the user clicks a section pill — temporarily mutes scroll detection. */
  notifyClick: () => void
}

/**
 * Returns the active section index based on scroll position. A section becomes
 * active when its top edge crosses the viewport midpoint. At the bottom of the
 * page the last section is forced active so the highlight tracks visible
 * content even past the last midpoint crossing.
 *
 * Deliberately uses a single `scroll` listener rather than `IntersectionObserver`
 * — simpler, more deterministic across overlapping sections, and matches the
 * pattern used by the legacy imandra-web page-nav.
 */
export function useActiveSection(
  sectionIds: ReadonlyArray<string>,
  { ignoreScrollMs = 700 }: UseActiveSectionOptions = {},
): UseActiveSectionResult {
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const ignoreScrollUntil = useRef(0)

  useEffect(() => {
    const update = () => {
      if (Date.now() < ignoreScrollUntil.current) return

      const elements = sectionIds
        .map((id) => document.getElementById(id))
        .filter((el): el is HTMLElement => el !== null)

      if (
        window.scrollY + window.innerHeight >=
        document.body.scrollHeight - 4
      ) {
        setActiveIndex(sectionIds.length - 1)
        return
      }

      const midpoint = window.innerHeight / 2
      let next: number | null = null
      for (let i = 0; i < elements.length; i++) {
        if (elements[i].getBoundingClientRect().top <= midpoint) {
          next = i
        } else {
          break
        }
      }
      setActiveIndex(next)
    }

    update()
    window.addEventListener('scroll', update, { passive: true })
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
    }
    // sectionIds is a literal tuple in callers; identity changes only when
    // sections change, which is the expected re-bind trigger.
  }, [sectionIds])

  const notifyClick = () => {
    ignoreScrollUntil.current = Date.now() + ignoreScrollMs
  }

  return { activeIndex, notifyClick }
}
